import { useEffect, useState } from 'react';
import './Base64Image.scss';

const getViewerMaxBounds = (maxHeightOverride) => ({
  width: Math.min(860, Math.max(320, window.innerWidth - 96)),
  height: maxHeightOverride ?? Math.round(window.innerHeight * 0.68),
});

const fitImageToBounds = (naturalWidth, naturalHeight, maxWidth, maxHeight) => {
  if (!naturalWidth || !naturalHeight) {
    return { width: maxWidth, height: maxHeight };
  }

  const scale = Math.min(1, maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
  };
};

const getRotatedBounds = (width, height, angleDeg) => {
  const radians = (angleDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  return {
    width: Math.max(1, Math.ceil(width * cos + height * sin)),
    height: Math.max(1, Math.ceil(width * sin + height * cos)),
  };
};

/**
 * Displays a base64 image. Uses the simple inline layout for document
 * thumbnails; uses the scrollable viewer stage inside the document popup.
 */
const Base64Image = ({
  data,
  width,
  height,
  rotationAngle = 0,
  zoomLevel = 1,
  viewerMaxHeight,
  onLayoutChange,
}) => {
  const [baseSize, setBaseSize] = useState(null);

  const isViewerMode =
    rotationAngle !== 0 ||
    zoomLevel !== 1 ||
    width === '100%' ||
    width === '100';

  useEffect(() => {
    setBaseSize(null);
  }, [data]);

  useEffect(() => {
    if (typeof onLayoutChange === 'function') {
      requestAnimationFrame(() => onLayoutChange());
    }
  }, [rotationAngle, zoomLevel, onLayoutChange]);

  if (!isViewerMode) {
    return (
      <img
        src={data}
        alt="Base64 Image"
        width={width}
        height={height}
        style={{ padding: '5px' }}
      />
    );
  }

  const maxBounds = getViewerMaxBounds(viewerMaxHeight);
  const resolvedMaxWidth =
    width === '100%' || width === '100'
      ? maxBounds.width
      : Number(width) || maxBounds.width;

  const resolvedMaxHeight =
    height === 'auto' || height === undefined
      ? maxBounds.height
      : Number(height) || maxBounds.height;

  const fittedSize = baseSize || {
    width: resolvedMaxWidth,
    height: resolvedMaxHeight,
  };
  const zoomedWidth = Math.max(1, Math.round(fittedSize.width * zoomLevel));
  const zoomedHeight = Math.max(1, Math.round(fittedSize.height * zoomLevel));
  const layoutBounds = getRotatedBounds(zoomedWidth, zoomedHeight, rotationAngle);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setBaseSize(
      fitImageToBounds(
        naturalWidth,
        naturalHeight,
        resolvedMaxWidth,
        resolvedMaxHeight,
      ),
    );
    if (typeof onLayoutChange === 'function') {
      requestAnimationFrame(() => onLayoutChange());
    }
  };

  const rotationTransform = rotationAngle
    ? `rotate(${rotationAngle}deg)`
    : undefined;

  return (
    <div
      className="base64-image-canvas"
      style={{
        width: '100%',
        minHeight: layoutBounds.height,
      }}
    >
      <div
        className="base64-image-stage"
        style={{
          width: layoutBounds.width,
          height: layoutBounds.height,
        }}
      >
        <div
          className="base64-image-stage__inner"
          style={{
            width: zoomedWidth,
            height: zoomedHeight,
            transform: rotationTransform,
            transformOrigin: 'center center',
          }}
        >
          <img
            src={data}
            alt="Base64 Image"
            className="base64-image-stage__img"
            onLoad={handleImageLoad}
            style={{
              width: zoomedWidth,
              height: zoomedHeight,
              maxWidth: baseSize ? 'none' : resolvedMaxWidth,
              maxHeight: baseSize ? 'none' : resolvedMaxHeight,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Base64Image;
